import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

export default {
    apply: 'FormSteps',
    setup(props, context, component) {
        const mobileOpen = ref(false)

        // Localized strings
        const t = (key) => {
            const form = component.form$?.value
            if (!form) return key
            const i18n = form.$vueform?.i18n
            if (!i18n) return key
            const locale = i18n.locale || 'de'
            const trans = i18n.locales?.[locale]?.festiware?.steps || {}
            return trans[key] || key
        }

        // Count of invalid steps (excluding the active one)
        const invalidStepCount = computed(() => {
            const steps = component.steps$?.value
            if (!steps) return 0
            let count = 0
            Object.values(steps).forEach((step) => {
                if (step.invalid && !step.active) count++
            })
            return count
        })

        const canScrollLeft = ref(false)
        const canScrollRight = ref(false)
        const hasOffscreenErrors = ref(false)
        const listEl = ref(null)

        // --- Sticky navigation ---
        // CSS position:sticky runs in the browser compositor — unaffected by JS
        // scroll-event throttling (Firefox APZ warning, iOS momentum scrolling).
        // JS scroll listeners caused "scroll-linked positioning effect" warnings and
        // unreliable behavior on mobile. IntersectionObserver sentinel is used only
        // to detect the stuck state for the box-shadow class; it does not drive
        // the actual positioning.
        const anchorEl = ref(null)
        const isStuck = ref(false)
        const stickyTopOffset = ref(0)   // negative padding-top of scroll container
        let stickyObserver = null
        let stickySentinel = null
        let scrollerRO = null
        let ro = null
        let mo = null

        // position:sticky on the anchor; top offset cancels scroll-container
        // padding-top so the nav sticks at the visual top (UIkit modal: 50px desktop).
        const anchorStyle = computed(() => ({
            position: 'sticky',
            top: stickyTopOffset.value + 'px',
            zIndex: 50,
        }))

        const findScrollAncestor = (el) => {
            let p = el?.parentElement
            while (p) {
                const cs = getComputedStyle(p)
                if (/(auto|scroll)/.test(cs.overflowY) || /(auto|scroll)/.test(cs.overflow)) return p
                p = p.parentElement
            }
            return null
        }

        const setNavRef = (el) => {
            anchorEl.value = el
        }

        const toggleMobile = () => {
            mobileOpen.value = !mobileOpen.value
        }

        const closeMobile = () => {
            mobileOpen.value = false
        }

        const updateScrollState = () => {
            const el = listEl.value
            if (!el) return

            const tolerance = 2
            canScrollLeft.value = el.scrollLeft > tolerance
            canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance

            // Check for off-screen invalid steps
            const invalidSteps = el.querySelectorAll('.vf-step-invalid')
            let offscreen = false
            invalidSteps.forEach((step) => {
                const rect = step.getBoundingClientRect()
                const containerRect = el.getBoundingClientRect()
                if (rect.right < containerRect.left || rect.left > containerRect.right) {
                    offscreen = true
                }
            })
            hasOffscreenErrors.value = offscreen
        }

        const scrollBy = (direction) => {
            const el = listEl.value
            if (!el) return
            const amount = el.clientWidth * 0.6
            el.scrollBy({ left: direction * amount, behavior: 'smooth' })
        }

        const scrollToFirstError = () => {
            const el = listEl.value
            if (!el) return
            const containerRect = el.getBoundingClientRect()
            const invalidSteps = el.querySelectorAll('.vf-step-invalid')
            for (const step of invalidSteps) {
                const rect = step.getBoundingClientRect()
                if (rect.right < containerRect.left || rect.left > containerRect.right) {
                    el.scrollBy({ left: rect.left - containerRect.left - 16, behavior: 'smooth' })
                    return
                }
            }
        }

        const scrollToActive = () => {
            const el = listEl.value
            if (!el) return
            const activeStep = el.querySelector('.vf-step-active')
            if (!activeStep) return

            const containerRect = el.getBoundingClientRect()
            const stepRect = activeStep.getBoundingClientRect()

            if (stepRect.left < containerRect.left) {
                el.scrollBy({ left: stepRect.left - containerRect.left - 16, behavior: 'smooth' })
            } else if (stepRect.right > containerRect.right) {
                el.scrollBy({ left: stepRect.right - containerRect.right + 16, behavior: 'smooth' })
            }
        }

        // See tabsPlugin.js setListRef for the rationale: Vue 3 function refs fire on
        // every update, and inline scrollLeft reads force sync layout + CSS variable
        // re-resolution — a Firefox-Stylo hot path that froze the contributor portal.
        const setListRef = (el) => {
            if (listEl.value === el) return
            if (listEl.value) listEl.value.removeEventListener('scroll', updateScrollState)
            listEl.value = el
            if (el) {
                el.addEventListener('scroll', updateScrollState, { passive: true })
                requestAnimationFrame(updateScrollState)
            }
        }

        // Close dropdown when active step changes & scroll to active
        if (component.current$) {
            watch(() => component.current$?.value?.name, () => {
                mobileOpen.value = false
                nextTick(() => {
                    scrollToActive()
                    updateScrollState()
                })
            })
        }

        onMounted(() => {
            nextTick(() => {
                const el = listEl.value
                if (!el) return
                updateScrollState()
                scrollToActive()

                ro = new ResizeObserver(() => updateScrollState())
                ro.observe(el)

                mo = new MutationObserver(() => updateScrollState())
                mo.observe(el, { attributes: true, subtree: true, attributeFilter: ['class'] })

                const anchor = anchorEl.value
                if (anchor && anchor.parentNode) {
                    const scroller = findScrollAncestor(anchor)

                    // top = visualViewport.offsetTop − scroller.paddingTop
                    // visualViewport.offsetTop is non-zero when the browser UI
                    // (address bar, Samsung multi-window title bar) overlays the
                    // layout viewport from above — sticky must compensate so the
                    // nav lands at the top of the visually accessible area.
                    const measureOffset = () => {
                        const visualOffset = window.visualViewport?.offsetTop || 0
                        const padTop = scroller
                            ? parseFloat(getComputedStyle(scroller).paddingTop) || 0
                            : 0
                        stickyTopOffset.value = visualOffset - padTop
                    }
                    measureOffset()
                    window.visualViewport?.addEventListener('scroll', measureOffset, { passive: true })
                    window.visualViewport?.addEventListener('resize', measureOffset, { passive: true })
                    if (scroller && typeof ResizeObserver !== 'undefined') {
                        scrollerRO = new ResizeObserver(measureOffset)
                        scrollerRO.observe(scroller)
                    }

                    stickySentinel = document.createElement('div')
                    stickySentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;'
                    anchor.parentNode.insertBefore(stickySentinel, anchor)
                    stickyObserver = new IntersectionObserver(
                        (entries) => { isStuck.value = !entries[0].isIntersecting },
                        { root: scroller || null, threshold: [0, 1] }
                    )
                    stickyObserver.observe(stickySentinel)
                }
            })
        })

        onBeforeUnmount(() => {
            if (stickyObserver) stickyObserver.disconnect()
            if (stickySentinel && stickySentinel.parentNode) stickySentinel.parentNode.removeChild(stickySentinel)
            if (scrollerRO) scrollerRO.disconnect()
            if (ro) ro.disconnect()
            if (mo) mo.disconnect()
            const el = listEl.value
            if (el) el.removeEventListener('scroll', updateScrollState)
        })

        return {
            ...component,
            mobileOpen,
            invalidStepCount,
            canScrollLeft,
            canScrollRight,
            hasOffscreenErrors,
            isStuck,
            anchorStyle,
            setNavRef,
            toggleMobile,
            closeMobile,
            scrollBy,
            scrollToFirstError,
            setListRef,
            t,
        }
    }
}
